import { Plus, X } from "lucide-react";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { FloatingButton } from "@/components/common/FloatingButton";
import { Loader } from "@/components/common/Loader";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { useProducts } from "@/features/products/hooks/useProducts";
import RecipeDetailsDialog from "@/features/recipes/components/RecipeDetailsDialog";
import { RecipeListItem } from "@/features/recipes/components/RecipeListItem";
import { useDeleteRecipe, useRecipes } from "@/features/recipes/hooks/useRecipes";
import RecipeFormPage from "@/features/recipes/RecipeFormPage";
import type { Recipe } from "@/features/recipes/types";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { toErrorMessage } from "@/lib/swr";

export default function RecipesPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editing, setEditing] = useState<Recipe | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Recipe | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const isDesktop = useIsDesktop();
  const toast = useToast();

  const { recipes, isLoading, error, refresh } = useRecipes();
  const { products } = useProducts();
  const { remove, isDeleting } = useDeleteRecipe();

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return recipes;
    return recipes.filter(
      (recipe) => recipe.name.toLowerCase().includes(term) || recipe.description?.toLowerCase().includes(term),
    );
  }, [recipes, searchTerm]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const name = pendingDelete.name;
    setDeleteError(null);
    try {
      await remove(pendingDelete.id);
      toast.success(`Recette « ${name} » supprimée.`);
      setPendingDelete(null);
    } catch (e) {
      const message = toErrorMessage(e) ?? "Le serveur n’a pas précisé la cause.";
      setDeleteError(`Suppression impossible. ${message} Vérifiez la cause puis réessayez.`);
    }
  };

  if (isCreating || editing) {
    return (
      <RecipeFormPage
        recipe={editing ?? undefined}
        onBack={() => {
          setIsCreating(false);
          setEditing(null);
          refresh();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Recettes"
        subtitle="Ce qu’il faut consommer pour fabriquer un produit"
        action={
          isDesktop ? (
            <Button size="lg" onClick={() => setIsCreating(true)}>
              <Plus />
              Nouvelle recette
            </Button>
          ) : null
        }
      />

      <SearchInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Rechercher une recette…"
      />

      {isLoading && <Loader />}

      {!isLoading && error && <ErrorState message={error} onRetry={() => refresh()} />}

      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState
          title={searchTerm ? "Aucune recette ne correspond" : "Aucune recette"}
          hint={
            searchTerm ? "Essayez un autre nom." : "Une recette relie un produit aux matières premières qu’il consomme."
          }
          action={
            searchTerm ? (
              <Button type="button" variant="outline" onClick={() => setSearchTerm("")}>
                <X />
                Effacer la recherche
              </Button>
            ) : (
              <Button type="button" onClick={() => setIsCreating(true)}>
                <Plus />
                Nouvelle recette
              </Button>
            )
          }
        />
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <div className="flex flex-col gap-3">
          {filtered.map((recipe) => (
            <RecipeListItem
              key={recipe.id}
              recipe={recipe}
              productName={
                recipe.productId ? products.find((product) => product.id === recipe.productId)?.name : undefined
              }
              subtitle={
                recipe.createdAt
                  ? `créée le ${DateTime.fromISO(recipe.createdAt).setLocale("fr").toLocaleString(DateTime.DATE_MED)}`
                  : undefined
              }
              onClick={() => setSelectedRecipe(recipe)}
            />
          ))}
        </div>
      )}

      {!isDesktop && <FloatingButton icon={Plus} ariaLabel="Nouvelle recette" onClick={() => setIsCreating(true)} />}

      {selectedRecipe && (
        <RecipeDetailsDialog
          recipe={selectedRecipe}
          onClose={() => setSelectedRecipe(null)}
          formatDate={(date?: string) =>
            date ? DateTime.fromISO(date).setLocale("fr").toLocaleString(DateTime.DATETIME_MED) : ""
          }
          onEdit={() => {
            setEditing(selectedRecipe);
            setSelectedRecipe(null);
          }}
          onDelete={() => {
            setPendingDelete(selectedRecipe);
            setSelectedRecipe(null);
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={`Supprimer « ${pendingDelete.name} » ?`}
          description="Le produit qu’elle fabrique reste au catalogue, mais il ne pourra plus être produit."
          details="Les productions déjà enregistrées avec cette recette seront retirées de vos stocks."
          confirmLabel="Supprimer"
          isPending={isDeleting}
          error={deleteError}
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteError(null);
            setPendingDelete(null);
          }}
        />
      )}
    </div>
  );
}
